import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, Video } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryTab = ({ restaurantId, restaurant, onUpdate }) => {
  const [uploading, setUploading] = useState(false);

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => {
      formData.append('galleryFiles', file);
    });

    try {
      setUploading(true);
      await axios.post(`${API}/restaurant-profile/${restaurantId}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${files.length} file(s) uploaded successfully`);
      onUpdate();
    } catch (error) {
      console.error('Failed to upload files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleMenuUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => {
      formData.append('menuFiles', file);
    });

    try {
      setUploading(true);
      await axios.post(`${API}/restaurant-profile/${restaurantId}/menu-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${files.length} menu image(s) uploaded successfully`);
      onUpdate();
    } catch (error) {
      console.error('Failed to upload menu images:', error);
      toast.error('Failed to upload menu images');
    } finally {
      setUploading(false);
    }
  };

  const deleteGalleryImage = async (imageUrl) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      await axios.delete(`${API}/restaurant-profile/${restaurantId}/gallery`, {
        data: { imageUrl }
      });
      toast.success('Image deleted successfully');
      onUpdate();
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error('Failed to delete image');
    }
  };

  const deleteMenuImage = async (imageUrl) => {
    if (!window.confirm('Delete this menu image?')) return;

    try {
      await axios.delete(`${API}/restaurant-profile/${restaurantId}/menu-images`, {
        data: { imageUrl }
      });
      toast.success('Menu image deleted successfully');
      onUpdate();
    } catch (error) {
      console.error('Failed to delete menu image:', error);
      toast.error('Failed to delete menu image');
    }
  };

  const isVideo = (url) => {
    return /\.(mp4|avi|mov|wmv|mkv)$/i.test(url);
  };

  return (
    <div className="space-y-8">
      {/* Front & Kitchen Images */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Images</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Front Image</Label>
              {restaurant.frontImage ? (
                <img src={restaurant.frontImage} alt="Front" className="w-full h-48 object-cover rounded-lg mt-2" />
              ) : (
                <div className="w-full h-48 bg-gray-100 rounded-lg mt-2 flex items-center justify-center text-gray-400">
                  No front image
                </div>
              )}
            </div>
            <div>
              <Label>Kitchen Image</Label>
              {restaurant.kitchenImage ? (
                <img src={restaurant.kitchenImage} alt="Kitchen" className="w-full h-48 object-cover rounded-lg mt-2" />
              ) : (
                <div className="w-full h-48 bg-gray-100 rounded-lg mt-2 flex items-center justify-center text-gray-400">
                  No kitchen image
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Menu Images */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Menu Images ({restaurant.menuImages?.length || 0})</CardTitle>
            <label htmlFor="menu-upload" className="cursor-pointer">
              <Button asChild disabled={uploading}>
                <span>
                  <Upload size={16} className="mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Menu Images'}
                </span>
              </Button>
            </label>
            <input
              id="menu-upload"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleMenuUpload}
              disabled={uploading}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {restaurant.menuImages?.map((image, index) => (
              <div key={index} className="relative group">
                <img
                  src={image}
                  alt={`Menu ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => deleteMenuImage(image)}
                >
                  <X size={16} />
                </Button>
              </div>
            ))}
            {(!restaurant.menuImages || restaurant.menuImages.length === 0) && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No menu images uploaded yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Gallery Images/Videos */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Gallery ({restaurant.galleryImages?.length || 0})</CardTitle>
            <label htmlFor="gallery-upload" className="cursor-pointer">
              <Button asChild disabled={uploading}>
                <span>
                  <Upload size={16} className="mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Gallery'}
                </span>
              </Button>
            </label>
            <input
              id="gallery-upload"
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={handleGalleryUpload}
              disabled={uploading}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {restaurant.galleryImages?.map((media, index) => (
              <div key={index} className="relative group">
                {isVideo(media) ? (
                  <div className="relative w-full h-32 bg-black rounded-lg overflow-hidden">
                    <video
                      src={media}
                      className="w-full h-full object-cover"
                      controls
                    />
                    <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      <Video size={12} />
                      Video
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={media}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      <ImageIcon size={12} />
                      Image
                    </div>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => deleteGalleryImage(media)}
                >
                  <X size={16} />
                </Button>
              </div>
            ))}
            {(!restaurant.galleryImages || restaurant.galleryImages.length === 0) && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No gallery items uploaded yet. Upload images and videos to showcase your restaurant!
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• <strong>Menu Images:</strong> Upload clear photos of your menu pages</li>
            <li>• <strong>Gallery:</strong> Showcase your restaurant ambience, dishes, and special events</li>
            <li>• <strong>Supported formats:</strong> Images (JPG, PNG, GIF, WEBP), Videos (MP4, AVI, MOV, WMV, MKV)</li>
            <li>• <strong>File size limit:</strong> Up to 50MB per file</li>
            <li>• <strong>Recommended:</strong> High-quality images with good lighting</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default GalleryTab;
